﻿<script lang="ts" setup>
import InputFieldHeader from '@/components/atoms/InputFieldHeader/InputFieldHeader.vue'
import { Loader } from '@googlemaps/js-api-loader'
import { whenever } from '@vueuse/core'
import { computed, onMounted, onUnmounted, ref, watch, WritableComputedRef } from 'vue'

import mapMarker from 'assets/images/map-marker--active.png'
import { useVModel } from '@/composables/useVModel'

enum MapType {
    ROADMAP = 'roadmap',
    SATELLITE = 'satellite'
}

const props = withDefaults(
    defineProps<{
        latitude?: number
        longitude?: number
        satellite?: boolean
        disableSwitching?: boolean
        editable?: boolean
        label?: string
        type?: string
        required?: boolean
        helpText?: string
        placeholder?: string
    }>(),
    {
        latitude: 55.71071790800002,
        longitude: 12.572880922359447,
        satellite: false
    }
)
const emit = defineEmits<{
    (e: 'update:latitude', lat: number): void
    (e: 'update:longitude', long: number): void
}>()
const _lat = useVModel(props, 'latitude', emit) as WritableComputedRef<number>
const _long = useVModel(props, 'longitude', emit) as WritableComputedRef<number>
const _value = computed<google.maps.LatLngLiteral>(() => ({ lat: _lat.value, lng: _long.value }))
const marker = ref<google.maps.Marker | null>(null)
const mapTypeRef = ref<MapType>(props.satellite ? MapType.SATELLITE : MapType.ROADMAP)
const map = ref<google.maps.Map | null>(null)
const mapRef = ref<HTMLElement | null>(null)
const loader = new Loader({
    apiKey: 'AIzaSyBmL2NKoyJPNSV8yllyapSCXgPcH-mXoio',
    version: 'weekly'
})
whenever(
    () => marker.value,
    () => (marker.value as google.maps.Marker).setPosition({ lat: _lat.value, lng: _long.value }),
    { deep: false }
)
whenever(
    () => [_lat, _long],
    () => {
        ;(marker.value as google.maps.Marker).setPosition({ lat: _lat.value, lng: _long.value })
    },
    { deep: true }
)
const watcher = watch([_lat, _long, marker, map, mapTypeRef], ([lat, long, newMarker, _map, mapType]) => {
    if (_map) {
        _map.setCenter(_value.value)
        _map.setMapTypeId(mapType)
    }
})

function initMap() {
    const map1 = new google.maps.Map(mapRef.value!, {
        center: {
            lat: _lat.value ?? 55.71071790800002,
            lng: _long.value ?? 12.572880922359447
        },
        mapTypeControl: false,
        streetViewControl: false,
        tilt: 0,
        zoom: 16
    })
    const marker1 = new google.maps.Marker({
        position: {
            lat: _lat.value ?? 55.71071790800002,
            lng: _long.value ?? 12.572880922359447
        },
        draggable: !!props.editable,
        map: map1,
        icon: mapMarker
    })
    marker1.addListener('mouseup', () => {
        _lat.value = marker1.getPosition()?.lat() as number
        _long.value = marker1.getPosition()?.lng() as number
    })
    marker.value = marker1
    map.value = map1
}

onMounted(() => {
    loader.load().then(initMap)
})
onUnmounted(() => {
    watcher()
})
</script>

<template>
    <div>
        <InputFieldHeader
            v-if="helpText || label || placeholder"
            :help-text="helpText"
            :label="label"
            :placeholder="placeholder"
        />
        <div
            v-if="!disableSwitching"
            class="mb-2"
        >
            <button
                :class="{ active: mapTypeRef === MapType.ROADMAP }"
                class="btn btn-outline map-btn me-4"
                @click="mapTypeRef = MapType.ROADMAP"
            >
                Brug Kort
            </button>
            <button
                :class="{ active: mapTypeRef === MapType.SATELLITE }"
                class="btn btn-outline map-btn"
                @click="mapTypeRef = MapType.SATELLITE"
            >
                Brug Satelit
            </button>
        </div>
        <div style="height: 30rem">
            <div
                ref="mapRef"
                class="mapdiv"
            ></div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.map-btn {
    color: $dn-blue;
    border-color: $dn-blue;
    background-color: $dn-white;

    &.active {
        color: $dn-white;
        background-color: $dn-blue;
    }
}

.mapdiv {
    width: 100%;
    height: 100%;
}
</style>
